import React, { useEffect, useRef, useState } from 'react'
import { Gem, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import GameBetsGroup, { BetPlayerRow } from '../../../components/GameBetsGroup'
import {
  WHEEL500_ZONE_CONFIG,
  WHEEL500_ZONE_KEYS,
  getWheel500ZoneMultiplier,
  isWheel500ResultStatus
} from '../config/wheel500Config'

/**
 * Лента ставок текущего раунда Wheel500.
 * Показывает аватар, имя, цвет, сумму. На фазе result подсвечивает win/lose.
 */

const SKIN_SWAP_PRELOAD_TIMEOUT = 1200
const COUNTER_DURATION = 1000

// Анимированный счётчик: плавно меняет число от value к value * multiplier
const AnimatedWinSum = ({ value, multiplier, isAnimating }) => {
  const target = value * multiplier
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)

  useEffect(() => {
    if (!isAnimating) {
      setDisplayValue(value)
      return
    }

    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(value + (target - value) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, value, target])

  return <>{displayValue.toLocaleString()}</>
}

// Анимированный счётчик для проигравших: плавно уменьшает число до 0
const AnimatedLoseSum = ({ value, isAnimating }) => {
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)

  useEffect(() => {
    if (!isAnimating) {
      setDisplayValue(value)
      return
    }

    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(value * (1 - eased))
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, value])

  return <>{Math.max(0, displayValue).toLocaleString()}</>
}

const getBetKey = (bet, zoneKey, index) => {
  const stableId = bet?.id ?? bet?.bet_id ?? bet?.created_at ?? null
  if (stableId !== null && stableId !== undefined && stableId !== '') {
    return `${zoneKey}-${stableId}`
  }

  return `${zoneKey}-${String(bet?.chat_id || 'anon')}-${Number(bet?.sum || 0)}-${index}`
}

const getBetAvatar = (bet) => {
  return bet?.ava || bet?.user?.ava || ''
}

const getBetSlug = (bet) => {
  return bet?.slug || bet?.user?.slug || null
}

const getBetName = (bet) => {
  return bet?.name || bet?.user?.name || ''
}

const getBetTeam = (bet) => {
  return bet?.team || bet?.user?.team || null
}

const AnimatedBetItems = ({ betItems, winSkin, isWinner, multiplier, crystalAmount, itemKeyPrefix }) => {
  const [showWinSkin, setShowWinSkin] = useState(false)
  const items = Array.isArray(betItems) ? betItems.slice(0, 6) : []
  const winSkinObj = winSkin && typeof winSkin === 'object' && !Array.isArray(winSkin) ? winSkin : null
  const winSkinsList = winSkinObj ? [winSkinObj] : []

  useEffect(() => {
    if (!isWinner || !winSkinObj || items.length === 0) {
      setShowWinSkin(false)
      return
    }

    let isCancelled = false
    let timeoutId = null

    const imageUrl = winSkinObj?.img
    const preloadPromise = imageUrl
      ? new Promise((resolve) => {
          const image = new window.Image()
          image.onload = resolve
          image.onerror = resolve
          image.src = imageUrl
        })
      : Promise.resolve()

    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(resolve, SKIN_SWAP_PRELOAD_TIMEOUT)
    })

    Promise.race([preloadPromise, timeoutPromise]).then(() => {
      if (isCancelled) return
      setShowWinSkin(true)
    })

    return () => {
      isCancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [isWinner, winSkinObj, items.length])

  const isWinView = showWinSkin && isWinner && winSkinsList.length > 0
  const renderedSkins = isWinView ? winSkinsList : items
  const hasDisplayedSkins = renderedSkins.length > 0
  const safeCrystalAmount = Number(crystalAmount || 0)

  if (!hasDisplayedSkins) {
    return null
  }

  return (
    <div className="mt-2.5 relative">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={isWinView ? 'win-skins' : 'base-skins'}
          initial={{ opacity: 0, scale: 0.96, filter: 'blur(2px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, scale: 1.03, filter: 'blur(3px)' }}
          transition={{ duration: 0.46, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-3 gap-1.5"
        >
          {renderedSkins.map((item, itemIndex) => (
            <motion.div
              key={`${item.id || itemIndex}-${itemKeyPrefix}`}
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, delay: Math.min(itemIndex, 3) * 0.05 }}
              className={`relative overflow-hidden rounded-md border p-1 flex flex-col items-center justify-center text-center ${isWinView ? 'border-accent-primary/45 bg-accent-primary/12 shadow-[0_0_16px_rgba(0,255,127,0.28)]' : 'border-white/10 bg-dark-primary/60'}`}
            >
              <div className="relative w-full h-10">
                <img
                  src={item.img}
                  alt={item.name}
                  className="w-full h-10 object-contain mx-auto"
                  loading="lazy"
                />
                {isWinView ? (
                  <motion.div
                    className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent"
                    animate={{ x: ['-120%', '140%'] }}
                    transition={{ duration: 0.7, ease: 'easeOut', delay: Math.min(itemIndex, 3) * 0.08 }}
                  />
                ) : null}
              </div>
              <p className={`mt-0.5 text-[9px] inline-flex items-center justify-center gap-0.5 w-full ${isWinView ? 'text-accent-primary font-medium' : 'text-accent-primary'}`}>
                {Number(item.cost ?? item.price ?? 0).toLocaleString()} <Gem className="w-2.5 h-2.5" />
              </p>
            </motion.div>
          ))}

          {hasDisplayedSkins && safeCrystalAmount > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.3, delay: Math.min(renderedSkins.length, 3) * 0.05 }}
              className="rounded-md border border-cyan-300/30 bg-cyan-500/10 p-1 flex flex-col items-center justify-center text-center"
            >
              <div className="w-full h-10 flex items-center justify-center">
                <Gem className="w-5 h-5 text-cyan-200" />
              </div>
              <p className="mt-0.5 text-[9px] text-cyan-100 inline-flex items-center justify-center gap-0.5 w-full">
                {isWinView
                  ? Math.round(safeCrystalAmount * multiplier).toLocaleString()
                  : safeCrystalAmount.toLocaleString()
                } <Gem className="w-2.5 h-2.5" />
              </p>
            </motion.div>
          ) : null}
        </motion.div>
      </AnimatePresence>

      {isWinView && items.length > 1 && (
        <div className="mt-1 flex items-center justify-center gap-1 text-[9px] text-white/45">
          <Sparkles className="h-2.5 w-2.5 text-white/35" />
          {items.length} → 1 · ×{multiplier}
        </div>
      )}
    </div>
  )
}

const Wheel500BetsFeed = ({ bets, status, winColor, disableAnimation = false }) => {
  const { t } = useTranslation()
  const isResult = isWheel500ResultStatus(status)
  const shouldAnimateResultAmounts = isResult && !disableAnimation
  const [expandedMobileColor, setExpandedMobileColor] = useState(null)

  useEffect(() => {
    setExpandedMobileColor(null)
  }, [status])

  const groupedBets = WHEEL500_ZONE_KEYS.reduce((acc, zoneKey) => {
    acc[zoneKey] = bets.filter((bet) => bet.zone === zoneKey).sort((a, b) => Number(b?.sum || 0) - Number(a?.sum || 0))
    return acc
  }, {})

  const columns = WHEEL500_ZONE_KEYS.map((zoneKey) => {
    const zoneCfg = WHEEL500_ZONE_CONFIG[zoneKey]
    const columnBets = groupedBets[zoneKey] || []
    // Показываем группы ставок без затемнения на всех стадиях раунда
    return {
      zoneKey,
      zoneCfg,
      columnBets,
      total: columnBets.reduce((sum, bet) => sum + Number(bet?.sum || 0), 0),
      playersCount: new Set(columnBets.map((bet, index) => String(bet.chat_id || bet.user?.id || `${getBetName(bet) || 'player'}-${index}`))).size
    }
  })

  const renderBetCard = (bet, index, zoneKey, zoneCfg, isWinBet, isLoseBet) => {
    const avatar = getBetAvatar(bet)
    const name = getBetName(bet)
    const team = getBetTeam(bet)
    const betItems = Array.isArray(bet.bet_items) ? bet.bet_items : []
    const multiplier = getWheel500ZoneMultiplier(bet?.zone) || Number(zoneCfg?.multiplier || 1)
    const isMyBet = Boolean(bet?.isMine || bet?.your_bet || bet?.you)
    const betSum = Number(bet.sum || 0)

    return (
      <motion.div
        key={getBetKey(bet, zoneKey, index)}
        layout
        initial={{ opacity: 0, y: -18, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.98 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className={`rounded-xl border p-2.5 ${
          isWinBet ? 'border-emerald-300/20 bg-emerald-500/[0.07]' : 'border-white/10 bg-surface'
        } ${isMyBet ? '!border-accent-primary/55 !bg-accent-primary/12 ring-1 ring-accent-primary/40 shadow-[0_0_16px_rgba(0,255,127,0.2)]' : ''}`}
      >
        <BetPlayerRow
          name={name || t('wheel500.betsFeed.player')}
          avatar={avatar}
          team={team}
          slug={getBetSlug(bet)}
          amount={betSum}
          amountClassName={isWinBet ? 'text-emerald-400' : isLoseBet ? 'text-red-400' : 'text-accent-primary'}
          iconClassName={isWinBet ? 'text-emerald-400' : isLoseBet ? 'text-red-400' : 'text-accent-primary/80'}
          amountContent={isWinBet && shouldAnimateResultAmounts
            ? <AnimatedWinSum value={betSum} multiplier={multiplier} isAnimating={shouldAnimateResultAmounts} />
            : isLoseBet && shouldAnimateResultAmounts
              ? <AnimatedLoseSum value={betSum} isAnimating={shouldAnimateResultAmounts} />
              : null}
        />

        <AnimatedBetItems
          betItems={betItems}
          winSkin={bet.win_skin}
          isWinner={isWinBet}
          multiplier={multiplier}
          crystalAmount={Number(bet.crystal || 0)}
          itemKeyPrefix={`${zoneKey}-${bet.chat_id || 'anon'}-${bet.id || index}`}
        />
      </motion.div>
    )
  }

  return (
    <div className="rounded-2xl bg-surface/30">
      {/* <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] md:text-xs font-semibold uppercase tracking-wide text-text-secondary">Ставки по цветам</h3>
        <span className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-white/80">{bets.length}</span>
      </div> */}

      <div className="space-y-2 lg:hidden">
        {columns.map(({ zoneKey, zoneCfg, columnBets, total, playersCount }) => {
          const isExpanded = expandedMobileColor === zoneKey
          return (
            <GameBetsGroup
              key={zoneKey}
              id={`wheel500-${zoneKey}`}
              label={t('wheel500.betsFeed.zone', { multiplier: zoneCfg.multiplier })}
              multiplier={zoneCfg.multiplier}
              color={zoneCfg.hex}
              total={total}
              playersCount={playersCount}
              bets={columnBets}
              isOpen={isExpanded}
              onToggle={() => setExpandedMobileColor(isExpanded ? null : zoneKey)}
              renderBet={(bet, index) => renderBetCard(bet, index, zoneKey, zoneCfg, isResult && bet.zone === winColor, isResult && winColor && bet.zone !== winColor)}
              emptyText={t('wheel500.betsFeed.empty')}
              maxListHeight
            />
          )
        })}
      </div>

      <div className="hidden lg:grid lg:grid-cols-4 lg:gap-3">
        {columns.map(({ zoneKey, zoneCfg, columnBets }) => (
          <div key={`desktop-${zoneKey}`}>
            <div className="mb-2.5 flex items-center justify-between hidden">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-white/90">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: zoneCfg.hex }} />
                {zoneCfg.label}
                <span className="font-semibold" style={{ color: `${zoneCfg.hex}cc` }}>×{zoneCfg.multiplier}</span>
              </span>
              <span className="text-xs text-text-secondary">{columnBets.length}</span>
            </div>

            {columnBets.length === 0 ? (
              <p className="py-5 text-center text-xs text-text-secondary">{t('wheel500.betsFeed.empty')}</p>
            ) : (
              <div className="space-y-1.5">
                {columnBets.map((bet, index) => {
                  const isWinBet = isResult && bet.zone === winColor
                  const isLoseBet = isResult && winColor && bet.zone !== winColor
                  return renderBetCard(bet, index, zoneKey, zoneCfg, isWinBet, isLoseBet)
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default Wheel500BetsFeed
